import { useEffect, useState } from "react";
import { bindActionCreators, Dispatch } from "redux";
import { connect } from "react-redux";
import { WithStyles, withStyles } from "@mui/styles";
import { Button, CircularProgress } from "@mui/material";
import _ from "lodash";
import { format, startOfToday, subYears } from "date-fns";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import classnames from "classnames";

// Contexts
import { withTranslate } from "../../core/TenantProvider";
// Components - Atoms, Organisms,Containers
import APSection from "./../../components/atoms/APSection";
import APSeriesTable from "./../../components/organisms/APSeriesTable";
import APErrorMessage from "../../components/atoms/APErrorMessage";
import { APDOBInput } from "../../components/atoms/APInput";
// Files in the same folder
import InvoicesContainerStyles from "./styles/InvoicesContainer.style";
import InvoicesIcon from "./assets/invoicesIcon.svg";
import { ReactComponent as DownloadIcon } from "./../../components/atoms/APDropDownMenu/assets/icons/icon-download.svg";
// Reducer
import { actions as InvoicesActions } from "./../../redux/modules/invoices";
import { ReducerState } from "./../../redux/modules";
import { FileListType } from "../../redux/modules/invoices/reducer";
// Utils
import { DownloadFile } from "../../core/utils/DownloadFile";
// Constants
import {
  DEFAULT_BREAKPOINT,
  ZIP_MIME_TYPE,
} from "../../core/utils/Constants/Constants";
import APDropDownMenu from "../../components/atoms/APDropDownMenu";

type AnnualStatementBanner = "notfound" | "failed" | null;

interface InvoicesContainerProps extends WithStyles<
  typeof InvoicesContainerStyles
> {
  getInvoicesSummary: Function;
  results: any;
  loading: string;
  translate: Function;
  annualStatementDownloadLoading: string;
  fileList: FileListType[] | null;
  downloadAnnualStatementFile: Function;
  resetGenerateStatementStoreData: Function;
  launchDarklyFlags: LDFlagSet;
}

const InvoicesContainer: React.FC<InvoicesContainerProps> = (props) => {
  const {
    classes,
    translate,
    results,
    loading,
    getInvoicesSummary,
    downloadAnnualStatementFile,
    annualStatementDownloadLoading,
    resetGenerateStatementStoreData,
    fileList,
    launchDarklyFlags = {},
  } = props;

  const { downloadAnnualStatement } = launchDarklyFlags || {};

  const previousYear: string = format(subYears(new Date(), 1), "yyyy");
  const [taxYear, setTaxYear] = useState<string | Date | null>(previousYear);
  const [taxYearError, setTaxYearError] = useState<boolean>(false);
  const [annualStatementBanner, setAnnualStatementBanner] =
    useState<AnnualStatementBanner>(null);
  const [summaryLoadErrorDismissed, setSummaryLoadErrorDismissed] =
    useState<boolean>(false);
  const [affiliateNumber, setSelectedAffilateNumber] = useState<string>("All");
  const [filteredInvoices, setFiliteredInvoices] = useState<any>(results);
  const [afflnoDropDownLabels, setAfflNoDropDownLables] = useState<any>([]);
  const recordNotFound: string = translate(
    "invoices.section.annualStatements.recordNotFound",
  );

  useEffect(() => {
    getInvoicesSummary(translate);
  }, []);

  useEffect(() => {
    if (loading === "LOADING") {
      setSummaryLoadErrorDismissed(false);
    }
  }, [loading]);

  useEffect(() => {
    if (taxYear === null) {
      setTaxYearError(true);
    } else {
      const maxYear = Number(format(subYears(new Date(), 1), "yyyy"));
      const minDate = new Date(2023, 0, 1);
      const selectedDate = new Date(taxYear);
      const selectedYear = selectedDate.getFullYear();

      if (selectedDate < minDate || selectedYear > maxYear) {
        setTaxYearError(true);
      } else {
        setTaxYearError(false);
      }
    }
  }, [taxYear]);

  useEffect(() => {
    let labels: Set<string> = new Set(["All"]);
    for (var x in results?.table) {
      var value = results?.table[x].fields[0].fieldValue;
      labels.add(value);
    }
    setAfflNoDropDownLables(
      [...labels].map((val) => ({
        label: val,
        value: val,
      })),
    );
    if ((affiliateNumber ?? "").toLowerCase() === "all") {
      setFiliteredInvoices(results);
    } else {
      setFiliteredInvoices({
        table:
          results?.table?.filter((data: any) => {
            return data.fields[0].fieldValue == affiliateNumber;
          }).length > 0
            ? results?.table?.filter((data: any) => {
                return data.fields[0].fieldValue == affiliateNumber;
              })
            : undefined,
      });
    }
  }, [affiliateNumber, results]);

  useEffect(() => {
    if (annualStatementDownloadLoading === "LOADING") {
      setAnnualStatementBanner(null);
      return;
    }

    if (
      annualStatementDownloadLoading !== "LOADED" &&
      annualStatementDownloadLoading !== "ERROR"
    ) {
      return;
    }

    if (annualStatementDownloadLoading === "LOADED") {
      const getFileList: FileListType[] | null = fileList ? fileList : null;

      if (getFileList?.length === 0) {
        setAnnualStatementBanner("notfound");
      }

      getFileList &&
        getFileList.forEach((file: FileListType) => {
          const { fileByte, fileName } = file;
          DownloadFile(fileName, fileByte, ZIP_MIME_TYPE).then(() => {
            resetGenerateStatementStoreData();
          });
        });

      resetGenerateStatementStoreData();
    }

    if (annualStatementDownloadLoading === "ERROR") {
      setAnnualStatementBanner("failed");
      resetGenerateStatementStoreData();
    }
  }, [annualStatementDownloadLoading, fileList]);

  useEffect(() => {
    if (!annualStatementBanner) {
      return;
    }

    const bannerTimeoutId = window.setTimeout(() => {
      setAnnualStatementBanner(null);
    }, 3000);

    return () => {
      window.clearTimeout(bannerTimeoutId);
    };
  }, [annualStatementBanner]);

  const handleFileDownload = () => {
    if (taxYear) {
      const formattedTaxYear: Date = new Date(taxYear);
      const year: string = format(formattedTaxYear, "yyyy");

      downloadAnnualStatementFile(year, affiliateNumber);
    }
  };

  const handleAnnualBannerClose = () => {
    setAnnualStatementBanner(null);
  };

  const annualStatementErrorMessage: string =
    annualStatementBanner === "notfound"
      ? recordNotFound
      : annualStatementBanner === "failed"
        ? translate("invoices.section.annualStatements.load.failed")
        : "";

  if (!results) return null;

  const NoDataView: JSX.Element = (
    <div className={classes.emptyData}>
      <img
        src={InvoicesIcon}
        alt={translate("invoices.section.invoice").toLowerCase()}
      />
      <h3>
        {translate("referrals.filters.nodata.header", {
          activeTabLabel: translate("invoices.section.invoice").toLowerCase(),
        })}
      </h3>
      <p>
        {translate("referrals.filters.nodata.subheader", {
          activeTabLabel: translate("invoices.section.invoice").toLowerCase(),
        })}
      </p>
    </div>
  );

  const DataView: JSX.Element = (
    <APSeriesTable
      hideEvent
      pagination={10}
      breakPoint={DEFAULT_BREAKPOINT}
      series={filteredInvoices.table}
      forInvoice={true}
    />
  );

  const currentView: JSX.Element =
    filteredInvoices && filteredInvoices.table ? DataView : NoDataView;
  const downloadAnnualStatementView: boolean = downloadAnnualStatement ?? true;

  return (
    <div className={classes.invoicesContainer}>
      {loading === "ERROR" && !summaryLoadErrorDismissed && (
        <APErrorMessage
          errorMessage={translate("invoices.error.summary.load.failed")}
          onClose={(event) => {
            event.preventDefault();
            setSummaryLoadErrorDismissed(true);
          }}
        />
      )}
      {annualStatementBanner && (
        <APErrorMessage
          errorMessage={annualStatementErrorMessage}
          onClose={(event) => {
            event.preventDefault();
            handleAnnualBannerClose();
          }}
        />
      )}
      {loading === "LOADING" ? (
        <div className={classes.invoiceLoader}>
          <CircularProgress className={classes.invoiceLoaderSpinner} />
        </div>
      ) : loading === "ERROR" ? (
        <div className={classes.invoicesContainerContent}>
          <div className={classes.filterContainer}>
            <APSection
              shadow={false}
              padded={false}
              sectionHeaderClassName={classes.sectionHeaderInput}
              title={translate("invoices.section.invoices.title")}
            ></APSection>
          </div>
        </div>
      ) : (
        <div className={classes.invoicesContainerContent}>
          <div className={classes.filterContainer}>
            <APSection
              shadow={false}
              padded={false}
              sectionHeaderClassName={classnames(classes.sectionHeaderInput, {
                [classes.disableBottomMargin]: !results?.table,
              })}
              title={translate("invoices.section.invoices.title")}
            ></APSection>
            {downloadAnnualStatementView && results?.table && (
              <div className={classes.filterRight}>
                <APDOBInput
                  varient={"annual_statement"}
                  views={["year"]}
                  inputFormatType={"yyyy"}
                  onChange={(event: { target: { value: Date | null } }) => {
                    const selectedDate = event.target.value;

                    if (selectedDate === null) {
                      setTaxYear(null); // Clear the input
                    } else {
                      setTaxYear(selectedDate);
                    }
                  }}
                  value={taxYear}
                  minDate={new Date(2023, 0, 1)}
                  maxDate={subYears(startOfToday(), 1)}
                  className={classes.legalEntityInput}
                  placeholderLabel={translate(
                    "invoices.section.tax.year.label",
                  )}
                  error={taxYearError}
                />
                {annualStatementDownloadLoading === "LOADING" && (
                  <CircularProgress className={classes.loadingIcon} size={18} />
                )}
                <APDropDownMenu
                  className={classes.legalEntityInput}
                  label={affiliateNumber}
                  options={afflnoDropDownLabels}
                  onChange={(data: any) => {
                    setSelectedAffilateNumber(data);
                  }}
                />
                <Button
                  className={classes.downloadButton}
                  onClick={(event) => {
                    event.preventDefault();
                    handleFileDownload();
                  }}
                  color="primary"
                  disableRipple={true}
                  disabled={
                    annualStatementDownloadLoading === "LOADING" ||
                    !taxYear ||
                    taxYearError
                  }
                >
                  <DownloadIcon className={classes.downloadIcon} />
                  {translate(
                    "invoices.section.invoice.annual.statement.download",
                  )}
                </Button>
              </div>
            )}
          </div>

          {currentView}
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: ReducerState) => ({
  results: state.invoices.results,
  loading: state.invoices.loadingState,
  fileList: state.invoices.fileList,
  annualStatementDownloadLoading: state.invoices.annualStatementDownloadLoading,
  launchDarklyFlags: state.user.launchDarklyFlags,
});

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      getInvoicesSummary: InvoicesActions.getInvoicesSummary,
      downloadAnnualStatementFile: InvoicesActions.downloadAnnualStatementFile,
      resetGenerateStatementStoreData:
        InvoicesActions.resetGenerateStatementStoreData,
    },
    dispatch,
  );

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withTranslate(withStyles(InvoicesContainerStyles)(InvoicesContainer)));
